import React, { useState, useEffect } from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

function ShiftAnalyzer({ shiftData }) {
  const [processedData, setProcessedData] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterWarning, setFilterWarning] = useState('all'); // 'all', 'with-warning', 'without-warning'

  useEffect(() => {
    if (shiftData) {
      processShiftData(shiftData);
    }
  }, [shiftData]);

  const hasNightDifferential = (startTime, endTime) => {
    if (!startTime || !endTime) return false;

    const parseTime = (timeValue) => {
      if (!timeValue) return null;

      if (timeValue instanceof Date) {
        const hours = timeValue.getHours();
        const minutes = timeValue.getMinutes();
        if (hours === 0 && minutes === 0) return null;
        return hours + minutes / 60;
      }

      const timeStr = String(timeValue);
      let timePart;

      if (timeStr.includes('/')) {
        const parts = timeStr.split(' ');
        if (parts.length < 2) return null;
        timePart = parts[1];
      } else if (timeStr.includes('-') && timeStr.includes(':')) {
        const parts = timeStr.split(' ');
        if (parts.length < 2) return null;
        timePart = parts[1];
      } else {
        return null;
      }

      if (timePart === '00:00' || timePart === '00:00:00') return null;

      const [hours, minutes] = timePart.split(':').map(Number);
      return hours + minutes / 60;
    };

    const start = parseTime(startTime);
    const end = parseTime(endTime);

    if (start === null || end === null) return false;

    // Night differential: AFTER 10pm (22:00) to BEFORE 6am (6:00)
    if (start < 6) return true;
    if (start > 22) return true;
    if (end > 22) return true;
    if (end < 6 && end < start) return true;

    return false;
  };

  const formatShiftTime = (startTime, endTime) => {
    if (!startTime || !endTime) return null;

    const formatTime = (timeValue) => {
      if (!timeValue) return null;

      let hour;
      let minutes = 0;

      if (timeValue instanceof Date) {
        hour = timeValue.getHours();
        minutes = timeValue.getMinutes();
      } else {
        const timeStr = String(timeValue);
        let timePart;

        if (timeStr.includes('/')) {
          const parts = timeStr.split(' ');
          if (parts.length < 2) return null;
          timePart = parts[1];
        } else if (timeStr.includes('-') && timeStr.includes(':')) {
          const parts = timeStr.split(' ');
          if (parts.length < 2) return null;
          timePart = parts[1];
        } else {
          return null;
        }

        const timeParts = timePart.split(':');
        hour = parseInt(timeParts[0]);
        minutes = parseInt(timeParts[1]);
      }

      if ((hour === 0 && minutes === 0) || (hour === 24 && minutes === 0)) {
        return '12mn';
      }

      if (hour === 12 && minutes === 0) {
        return '12nn';
      }

      if (hour === 24) {
        hour = 0;
      }

      const ampm = hour >= 12 ? 'pm' : 'am';
      const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);

      if (minutes > 0) {
        const minuteStr = minutes < 10 ? `0${minutes}` : minutes;
        return `${displayHour}:${minuteStr}${ampm}`;
      }
      return `${displayHour}${ampm}`;
    };

    const start = formatTime(startTime);
    const end = formatTime(endTime);

    if (!start || !end) return null;
    return `${start}-${end}`;
  };

  const processShiftData = (data) => {
    // The data.processedData should already have staffGroups from backend
    if (data.processedData && data.processedData.staffGroups) {
      setProcessedData(data.processedData);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getFilteredStaffGroups = (staffGroups) => {
    if (!staffGroups) return staffGroups;

    switch (filterWarning) {
      case 'with-warning':
        return staffGroups.filter(staff => staff.hasTimeMismatch);
      case 'without-warning':
        return staffGroups.filter(staff => !staff.hasTimeMismatch);
      default:
        return staffGroups;
    }
  };

  const getSortedStaffGroups = (staffGroups) => {
    if (!sortConfig.key || !staffGroups) return staffGroups;

    return [...staffGroups].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const downloadExcel = async () => {
    if (!processedData || !processedData.staffGroups) return;

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Shift Organizer';
    workbook.created = new Date();

    const worksheet = workbook.addWorksheet('Shift Schedule');

    // Get filtered and sorted staff groups based on current filter
    const dataToExport = getSortedStaffGroups(getFilteredStaffGroups(processedData.staffGroups));

    // Get all unique dates across all staff (sorted)
    const allDates = new Set();
    dataToExport.forEach(staff => {
      if (staff.schedules) {
        staff.schedules.forEach(schedule => {
          allDates.add(new Date(schedule.date).getTime());
        });
      }
    });
    const sortedDates = Array.from(allDates).sort((a, b) => a - b).map(time => new Date(time));

    const formatDateHeader = (date) => {
      const day = date.getDate();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      return `${day}-${month}`;
    };

    // Filter out dates with no data
    const datesToInclude = sortedDates.filter(date => {
      return dataToExport.some(staff => {
        return staff.schedules && staff.schedules.some(s => new Date(s.date).getTime() === date.getTime());
      });
    });

    const dateHeaders = datesToInclude.map(d => formatDateHeader(d));

    // Define columns
    const columns = [
      { header: 'Warning', key: 'warning', width: 10 },
      { header: 'Payroll ID', key: 'payrollId', width: 12 },
      { header: 'D365 ID', key: 'd365Id', width: 15 },
      { header: 'Name', key: 'name', width: 28 },
      { header: 'Default Shift', key: 'defaultShift', width: 15 },
      ...dateHeaders.map((header, idx) => ({ header, key: `date_${idx}`, width: 12 }))
    ];

    worksheet.columns = columns;

    // Style header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF059669' } // Green header
    };
    headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
    headerRow.height = 22;

    // Add data rows
    dataToExport.forEach((staff, index) => {
      const scheduleMap = {};
      if (staff.schedules) {
        staff.schedules.forEach(schedule => {
          const dateKey = new Date(schedule.date).getTime();
          scheduleMap[dateKey] = schedule.shiftDisplay || schedule.shift || '';
        });
      }

      const rowData = {
        warning: staff.hasTimeMismatch ? '⚠️ YES' : '',
        payrollId: 'GSC00000',
        d365Id: staff.staffCode,
        name: staff.staffName,
        defaultShift: staff.defaultShift || 'N/A'
      };

      // Add schedule data for each date
      datesToInclude.forEach((date, idx) => {
        const value = scheduleMap[date.getTime()];
        rowData[`date_${idx}`] = value !== undefined ? value : '';
      });

      const row = worksheet.addRow(rowData);

      // Apply styling based on warning status
      if (staff.hasTimeMismatch) {
        // Light red background for warning rows
        row.eachCell((cell, colNumber) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFECACA' } // Light red
          };
          // Make warning column and name bold red
          if (colNumber === 1 || colNumber === 4) {
            cell.font = { bold: true, color: { argb: 'FFDC2626' } };
          }
        });
      } else {
        // Light green background for OK rows
        row.eachCell((cell) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD1FAE5' } // Light green
          };
        });
      }

      // Center align all cells except name
      row.eachCell((cell, colNumber) => {
        if (colNumber !== 4) { // Don't center-align name column
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        } else {
          cell.alignment = { vertical: 'middle' };
        }
      });
    });

    // Add borders to all cells
    worksheet.eachRow((row, rowNumber) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFD1D5DB' } },
          left: { style: 'thin', color: { argb: 'FFD1D5DB' } },
          bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } },
          right: { style: 'thin', color: { argb: 'FFD1D5DB' } }
        };
      });
    });

    // Freeze header row
    worksheet.views = [{ state: 'frozen', ySplit: 1 }];

    // Add auto-filter
    worksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: dataToExport.length + 1, column: columns.length }
    };

    // Generate filename with filter info
    let filterSuffix = '';
    if (filterWarning === 'with-warning') filterSuffix = '_warnings_only';
    else if (filterWarning === 'without-warning') filterSuffix = '_no_warnings';

    // Export file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `shift_schedule_${shiftData.fileName || 'export'}${filterSuffix}.xlsx`);
  };

  if (!processedData || !processedData.staffGroups) {
    return <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>No data to display</div>;
  }

  const staffGroups = processedData.staffGroups;
  const filteredStaffGroups = getFilteredStaffGroups(staffGroups);
  const sortedStaffGroups = getSortedStaffGroups(filteredStaffGroups);

  // Count warnings for display
  const warningCount = staffGroups.filter(s => s.hasTimeMismatch).length;
  const noWarningCount = staffGroups.length - warningCount;

  // Calculate grand totals
  const grandTotal = {
    hours: staffGroups.reduce((sum, staff) => sum + (staff.totals?.hours || 0), 0),
    standardHours: staffGroups.reduce((sum, staff) => sum + (staff.totals?.standardHours || 0), 0),
    breakHours: staffGroups.reduce((sum, staff) => sum + (staff.totals?.breakHours || 0), 0),
    overtimeHours: staffGroups.reduce((sum, staff) => sum + (staff.totals?.overtimeHours || 0), 0),
    nightWorkHours: staffGroups.reduce((sum, staff) => sum + (staff.totals?.nightWorkHours || 0), 0),
    overtimeNightHours: staffGroups.reduce((sum, staff) => sum + (staff.totals?.overtimeNightHours || 0), 0),
    overtimePublicHolidayHours: staffGroups.reduce((sum, staff) => sum + (staff.totals?.overtimePublicHolidayHours || 0), 0)
  };

  // Get all unique dates
  const allDatesSet = new Set();
  staffGroups.forEach(staff => {
    if (staff.schedules) {
      staff.schedules.forEach(s => {
        allDatesSet.add(new Date(s.date).getTime());
      });
    }
  });
  const sortedDates = Array.from(allDatesSet).sort((a, b) => a - b);

  const datesToShow = sortedDates.filter(timestamp => {
    return staffGroups.some(staff => {
      return staff.schedules && staff.schedules.some(s => new Date(s.date).getTime() === timestamp);
    });
  });

  return (
    <div>
      {/* Grand Total Summary */}
      <div className="grand-total-card">
        <div className="grand-total-header">📊 Grand Total Summary</div>
        <div className="grand-total-subheader">All Staff Combined ({staffGroups.length} staff members)</div>
        <div className="grand-total-stats">
          <div className="grand-total-stat">
            <div className="grand-total-stat-label">Total Hours</div>
            <div className="grand-total-stat-value">{grandTotal.hours.toFixed(1)}</div>
          </div>
          <div className="grand-total-stat">
            <div className="grand-total-stat-label">Standard Hours</div>
            <div className="grand-total-stat-value">{grandTotal.standardHours.toFixed(1)}</div>
          </div>
          <div className="grand-total-stat">
            <div className="grand-total-stat-label">Break Hours</div>
            <div className="grand-total-stat-value">{grandTotal.breakHours.toFixed(1)}</div>
          </div>
          <div className="grand-total-stat">
            <div className="grand-total-stat-label">Overtime Hours</div>
            <div className="grand-total-stat-value">{grandTotal.overtimeHours.toFixed(1)}</div>
          </div>
          <div className="grand-total-stat highlight-night">
            <div className="grand-total-stat-label">🌙 Night Work Hours</div>
            <div className="grand-total-stat-value">{grandTotal.nightWorkHours.toFixed(1)}</div>
          </div>
          <div className="grand-total-stat">
            <div className="grand-total-stat-label">OT Night Hours</div>
            <div className="grand-total-stat-value">{grandTotal.overtimeNightHours.toFixed(1)}</div>
          </div>
          <div className="grand-total-stat">
            <div className="grand-total-stat-label">OT Holiday Hours</div>
            <div className="grand-total-stat-value">{grandTotal.overtimePublicHolidayHours.toFixed(1)}</div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="legend">
        <div className="legend-item">
          <div className="legend-box day-shift"></div>
          <span>Day Shift (✓)</span>
        </div>
        <div className="legend-item">
          <div className="legend-box night-shift"></div>
          <span>Night Shift (Shows Time)</span>
        </div>
        <div className="legend-item">
          <div className="legend-box leave-day"></div>
          <span>Leave (LWP/LWOP)</span>
        </div>
        <div className="legend-item">
          <div className="legend-box holiday-day"></div>
          <span>Holiday</span>
        </div>
        <div className="legend-item">
          <span style={{color: '#dc3545', fontWeight: '600'}}>⚠️ Red Name</span>
          <span> - Shift time mismatch</span>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="filter-controls">
        <span className="filter-label">Filter by Warning:</span>
        <div className="filter-buttons">
          <button
            className={`filter-btn ${filterWarning === 'all' ? 'active' : ''}`}
            onClick={() => setFilterWarning('all')}
          >
            All ({staffGroups.length})
          </button>
          <button
            className={`filter-btn filter-btn-warning ${filterWarning === 'with-warning' ? 'active' : ''}`}
            onClick={() => setFilterWarning('with-warning')}
          >
            ⚠️ With Warning ({warningCount})
          </button>
          <button
            className={`filter-btn filter-btn-ok ${filterWarning === 'without-warning' ? 'active' : ''}`}
            onClick={() => setFilterWarning('without-warning')}
          >
            ✓ Without Warning ({noWarningCount})
          </button>
        </div>
      </div>

      {/* Interactive Table */}
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th
                className={`sortable ${sortConfig.key === 'staffCode' ? `sorted-${sortConfig.direction}` : ''}`}
                onClick={() => handleSort('staffCode')}>
                Staff Code
              </th>
              <th
                className={`sortable ${sortConfig.key === 'staffName' ? `sorted-${sortConfig.direction}` : ''}`}
                onClick={() => handleSort('staffName')}>
                Name
              </th>
              <th
                className={`sortable ${sortConfig.key === 'defaultShift' ? `sorted-${sortConfig.direction}` : ''}`}
                onClick={() => handleSort('defaultShift')}>
                Default Shift
              </th>
              {datesToShow.map((timestamp, idx) => {
                const date = new Date(timestamp);
                const dateStr = `${date.getDate()}-${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][date.getMonth()]}`;
                return <th key={idx}>{dateStr}</th>;
              })}
            </tr>
          </thead>
          <tbody>
            {sortedStaffGroups.map((staff, staffIdx) => {
              const scheduleMap = {};
              if (staff.schedules) {
                staff.schedules.forEach(s => {
                  scheduleMap[new Date(s.date).getTime()] = s;
                });
              }

              return (
                <tr key={staffIdx}>
                  <td><strong>{staff.staffCode}</strong></td>
                  <td className={staff.hasTimeMismatch ? 'time-mismatch-text' : ''}>
                    {staff.staffName}
                    {staff.hasTimeMismatch && <span style={{marginLeft: '5px', fontSize: '0.9em'}}>⚠️</span>}
                  </td>
                  <td>{staff.defaultShift || 'N/A'}</td>
                  {datesToShow.map((timestamp, idx) => {
                    const schedule = scheduleMap[timestamp];
                    if (!schedule) {
                      return <td key={idx}>—</td>;
                    }

                    let cellClass = '';
                    const display = schedule.shiftDisplay || schedule.shift || '';

                    if (display === 'HOLIDAY') {
                      cellClass = 'holiday-day';
                    } else if (display === 'LWP' || display === 'LWOP') {
                      cellClass = 'leave-day';
                    } else if (schedule.hasNight) {
                      cellClass = 'night-shift';
                    } else {
                      cellClass = 'day-shift';
                    }

                    return (
                      <td key={idx} className={cellClass}>
                        {display === '√' ? (
                          <span className="checkmark">✓</span>
                        ) : (
                          display
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Download Button */}
      <button onClick={downloadExcel} className="download-btn" style={{marginTop: '20px'}}>
        📥 Download Excel Report ({staffGroups.length} staff)
        {sortConfig.key && (
          <span style={{fontSize: '0.85em', opacity: 0.9, marginLeft: '10px'}}>
            - Sorted by {sortConfig.key === 'staffCode' ? 'Staff Code' : sortConfig.key === 'staffName' ? 'Name' : 'Default Shift'} ({sortConfig.direction === 'asc' ? '↑' : '↓'})
          </span>
        )}
      </button>

      {/* Showing filtered results info */}
      {filterWarning !== 'all' && (
        <div style={{ textAlign: 'center', marginTop: '10px', color: '#666', fontSize: '0.9em' }}>
          Showing {sortedStaffGroups.length} of {staffGroups.length} staff members
        </div>
      )}
    </div>
  );
}

export default ShiftAnalyzer;
